import { defineStore } from 'pinia'

let nextToastId = 1
const timeoutHandles = new Map()
const DEFAULT_TOAST_DURATION = 5000
const DEFAULT_ERROR_TOAST_DURATION = 9000

const resolveToastDuration = (options, variant) => {
  if (Number.isFinite(options.duration) && Number(options.duration) > 0) {
    return Number(options.duration)
  }

  if (Number.isFinite(options.durationSeconds) && Number(options.durationSeconds) > 0) {
    return Number(options.durationSeconds) * 1000
  }

  if (variant === 'error') {
    return DEFAULT_ERROR_TOAST_DURATION
  }

  return DEFAULT_TOAST_DURATION
}

export const useToastsStore = defineStore('toasts', {
  state: () => ({
    items: [],
  }),
  actions: {
    show(options = {}) {
      const id = String(options.id ?? `toast-${Date.now()}-${nextToastId++}`)
      const variant = options.variant ?? 'info'
      const persistent = Boolean(options.persistent)
      const duration = persistent ? null : resolveToastDuration(options, variant)
      const normalizedActions = Array.isArray(options.actions)
        ? options.actions.map((action) => ({
          label: String(action?.label ?? '').trim(),
          variant: action?.variant ?? 'ghost',
          class: action?.class ?? null,
          onClick: typeof action?.onClick === 'function' ? action.onClick : null,
          closeOnClick: action?.closeOnClick !== false,
        })).filter((action) => action.label !== '')
        : []

      const toast = {
        id,
        variant,
        title: String(options.title ?? '').trim(),
        message: String(options.message ?? '').trim(),
        dismissible: options.dismissible !== false,
        persistent,
        duration,
        actions: normalizedActions,
      }

      this.items = [...this.items, toast]

      if (!persistent && duration !== null) {
        this.scheduleRemoval(id, duration)
      }

      return id
    },
    success(options = {}) {
      return this.show({ ...options, variant: 'success' })
    },
    error(options = {}) {
      return this.show({ ...options, variant: 'error' })
    },
    warning(options = {}) {
      return this.show({ ...options, variant: 'warning' })
    },
    info(options = {}) {
      return this.show({ ...options, variant: 'info' })
    },
    action(options = {}) {
      return this.show({
        ...options,
        variant: 'action',
        persistent: options.persistent ?? true,
        dismissible: options.dismissible ?? false,
      })
    },
    scheduleRemoval(id, duration) {
      this.clearScheduledRemoval(id)

      const handle = window.setTimeout(() => {
        this.remove(id)
      }, duration)

      timeoutHandles.set(String(id), handle)
    },
    clearScheduledRemoval(id) {
      const normalizedId = String(id)
      const handle = timeoutHandles.get(normalizedId)

      if (handle) {
        window.clearTimeout(handle)
        timeoutHandles.delete(normalizedId)
      }
    },
    remove(id) {
      const normalizedId = String(id)
      this.clearScheduledRemoval(normalizedId)
      this.items = this.items.filter((toast) => toast.id !== normalizedId)
    },
    clear() {
      this.items.forEach((toast) => this.clearScheduledRemoval(toast.id))
      this.items = []
    },
  },
})
